import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { useMutation, useQuery } from '@apollo/client';
import { captureException, Button } from '@playbookux/playbook-web-core';
import ModalWithRows from '../../../../../core/components/modal-with-rows';
import {
  GET_SAVED_FILTERS,
  CREATE_ANALYTICS_FILTER,
  GET_SURVEY_ANALYTICS_FILTER_OPTIONS,
  GET_PROJECT_QUESTIONS,
} from '../graphql';
import { mapProjectDemographics } from '../../../../../core/utils/map-project-analytics-data';
import { demographicsAttributes, questionTypeRanking } from './constants';
import SavedFiltersTab from './saved-filters-tab';
import NewFilterTab from './new-filter-tab';
import ParticipantsSource from '../../../../../core/constants/participantsSource';

const TABS = {
  NEW_FILTER: { key: 'NEW_FILTER', text: 'New filter' },
  SAVED_FILTER: { key: 'SAVED_FILTER', text: 'Use a saved filter' },
};

const FILTER_NAME = 'Untitled filter';

const FilterModal = ({
  open,
  onClose,
  projectId,
  onFilterApplied,
  scope,
  participantsSource,
}) => {
  const [selectedTab, setSelectedTab] = useState(TABS.NEW_FILTER.key);
  const [conditions, setConditions] = useState([
    {
      id: uuidv4(),
      attribute: '',
      operator: 'is',
      value: [],
    },
  ]);
  const [error, setError] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const newFilterSubmitRef = useRef(null);
  const [savedFilterApplyData, setSavedFilterApplyData] = useState({ canApply: false, apply: null });

  const handleChangeTab = useCallback((tabKey) => {
    setSelectedTab(tabKey);
  }, []);

  const [createFilter, { loading: creating }] = useMutation(CREATE_ANALYTICS_FILTER, {
    refetchQueries: [{ query: GET_SAVED_FILTERS, variables: { projectId } }],
  });

  const { data: questionsData, error: questionsError } = useQuery(GET_PROJECT_QUESTIONS, {
    variables: { projectId },
    skip: !open,
  });

  const {
    data,
    loading: loadingFilterOptions,
  } = useQuery(GET_SURVEY_ANALYTICS_FILTER_OPTIONS, {
    variables: { projectId },
    skip: !open,
    fetchPolicy: 'no-cache',
  });

  // Set attribute options separated by group to be organized and filtered in the dropdown
  useEffect(() => {
    if (questionsData) {
      const newAllQuestions = [];
      const groupedAttributeOptions = [];

      const addGroup = (key, text, options) => {
        groupedAttributeOptions.push({
          key,
          text,
          options,
        });
      };

      const processQuestions = (questionsArray, optionsArray, allQuestionsArray) => {
        questionsArray.forEach((question) => {
          if (question.questionOptions.length && question.questionType !== questionTypeRanking) {
            optionsArray.push({
              key: question.id,
              text: `${question.label} (${_.startCase(question.questionType)})`,
              value: question.id,
            });
            allQuestionsArray.push(question);
          }
        });
      };

      // Demographics group
      if (participantsSource === ParticipantsSource.PLAYBOOK) {
        const demographicsOptions = demographicsAttributes.map((attr) => ({
          key: attr,
          text: _.startCase(attr),
          value: attr,
        }));

        addGroup('demographics', 'Demographics', demographicsOptions);
      }

      // Screener survey group
      const screenerSurveyOptions = [];
      questionsData.project.segments.forEach((segment) => {
        segment.surveys.forEach((survey) => {
          processQuestions(survey.questions, screenerSurveyOptions, newAllQuestions);
        });
      });

      addGroup('screener_survey', 'Screener survey', screenerSurveyOptions);

      // Test questions group
      const testQuestionsOptions = [];
      questionsData.project.surveys.forEach((survey) => {
        if (survey.questions) {
          processQuestions(survey.questions, testQuestionsOptions, newAllQuestions);
        }
        if (survey.surveysPages) {
          survey.surveysPages.forEach((page) => {
            processQuestions(page.questions, testQuestionsOptions, newAllQuestions);
          });
        }
      });

      addGroup('test_questions', 'Test questions', testQuestionsOptions);

      setAttributeOptions(groupedAttributeOptions);
      setAllQuestions(newAllQuestions);
    }
    if (questionsError) {
      console.error('Error fetching questions:', questionsError);
    }
  }, [questionsData, questionsError]);

  // set demographic filter options for dropdown
  useEffect(() => {
    if (data && data.surveyAnalyticsFilterOptions) {
      const response = mapProjectDemographics(data.surveyAnalyticsFilterOptions);
      setFilterOptions(response);
    }
  }, [data]);

  const handleConditionChange = (conditionId, field, value) => {
    setConditions((prevConditions) => {
      const newConditions = prevConditions.map((cond) => {
        if (cond.id === conditionId) {
          return { ...cond, [field]: value, ...(field === 'attribute' ? { value: [] } : {}) };
        }
        return cond;
      });
      return newConditions;
    });
  };

  const addCondition = () => {
    setConditions([...conditions, {
      id: uuidv4(),
      attribute: '',
      operator: 'is',
      value: [],
    }]);
  };

  const removeCondition = (id) => {
    if (conditions.length > 1) {
      const newConditions = conditions.filter((condition) => condition.id !== id);
      setConditions(newConditions);
    }
  };

  const handleSavedFilter = async (conditionsParam, name) => {
    try {
      const result = await createFilter({
        variables: {
          attributes: {
            name,
            conditions: JSON.stringify(conditionsParam),
            projectId,
          },
        },
      });

      const newFilter = result.data.createAnalyticsFilter.analyticsFilter;

      onFilterApplied({
        id: newFilter.id,
        name: newFilter.name,
        conditions: conditionsParam,
        saved: true,
      });
    } catch (err) {
      captureException('CREATE_ANALYTICS_FILTER', err);
    }
  };

  const handleUnsavedFilter = (conditionsParam, name) => {
    onFilterApplied({
      name,
      saved: false,
      conditions: conditionsParam,
    });
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const hasEmpty = conditions.some((cond) => !cond.attribute
      || !cond.operator
      || (Array.isArray(cond.value) ? cond.value.length === 0 : !cond.value));

    if (hasEmpty) {
      setError(true);
      setSubmitting(false);
      return;
    }

    const conditionsPayload = conditions.map((cond) => ({
      attribute: cond.attribute,
      operator: cond.operator,
      value: cond.value,
    }));

    const filterName = values.name || FILTER_NAME;

    if (values.saveFilter && scope === 'user') {
      handleSavedFilter(conditionsPayload, filterName);
    } else {
      // share project scope or not saving filter
      handleUnsavedFilter(conditionsPayload, filterName);
    }

    onClose();
    setSubmitting(false);
  };

  const handleApplyButtonClick = async () => {
    if (selectedTab === TABS.NEW_FILTER.key) {
      if (newFilterSubmitRef.current) {
        newFilterSubmitRef.current();
      }
    } else if (selectedTab === TABS.SAVED_FILTER.key) {
      if (savedFilterApplyData.canApply && typeof savedFilterApplyData.apply === 'function') {
        savedFilterApplyData.apply();
      }
    }
  };

  const handleApplySavedFilter = (value) => {
    onFilterApplied(value);
    onClose();
  };

  const footer = (
    <Button
      primary
      onClick={handleApplyButtonClick}
      disabled={selectedTab === TABS.SAVED_FILTER.key && !savedFilterApplyData.canApply}
      loading={creating}
    >
      Apply filter
    </Button>
  );

  return (
    <ModalWithRows
      open={open}
      onClose={onClose}
      modalTitle="Filter results"
      tabs={TABS}
      selectedTab={selectedTab}
      onSelectTab={handleChangeTab}
      showTab
      footer={footer}
    >
      {selectedTab === TABS.NEW_FILTER.key && (
        <NewFilterTab
          conditions={conditions}
          handleConditionChange={handleConditionChange}
          addCondition={addCondition}
          removeCondition={removeCondition}
          filterOptions={filterOptions}
          onSubmit={handleSubmit}
          error={error}
          creating={creating}
          loadingFilterOptions={loadingFilterOptions}
          attributeOptions={attributeOptions}
          allQuestions={allQuestions}
          scope={scope}
          setSubmitForm={(submitFunc) => { newFilterSubmitRef.current = submitFunc; }}
        />
      )}
      {selectedTab === TABS.SAVED_FILTER.key && (
        <SavedFiltersTab
          projectId={projectId}
          allQuestions={allQuestions}
          onClose={onClose}
          scope={scope}
          setApplyFunction={setSavedFilterApplyData}
          onFilterApplied={handleApplySavedFilter}
        />
      )}
    </ModalWithRows>
  );
};

FilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  participantsSource: PropTypes.string.isRequired,
  onFilterApplied: PropTypes.func.isRequired,
};

export default FilterModal;
